import { BoxFC, BoxFR } from "components/BoxCustom";
import { GlobalStateContext } from "contexts/GlobalStateContext";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { initFilterData, clearFilterData } from "./initData"
import DateTimeSelect from "components/DateTimeSelect";
import ComboBox from "components/ComboBox";
import { fromReprComboId, getReprCombo, repairOrderApiNm, repairOrderColNm, toReprComboId } from "./constant";
import SearchButton from "components/buttons/SearchButton";
import ClearButton from "components/buttons/ClearButton";
import { columns } from "./columns";
import { Box, Button, ButtonGroup, MenuItem, TextField } from "@mui/material";
import DataGridCellExpand from "components/DataGridCellExpand/DataGridCellExpand";
import AddButton from "components/buttons/AddButton";
import RepairOrderDialog from "./RepairOrderDialog";
import { addIdForDataGrid } from "utils";
import { PrintRounded } from "resource/muiImport";
import { alertError } from "components/Alert";
import { printRepairOrder } from "branch/functions/printRepairOrder";

let selectedId = null
let lastFilter = null
const RepairOrderManagement = () => {
  const { ax, msData } = useContext(GlobalStateContext)

  const [dataTable, setDataTable] = useState([])
  const [filterData, setFilterData] = useState({ ...initFilterData })
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectionModel, setSelectionModel] = useState([])
  const [isPrinting, setIsPrinting] = useState(false)

  const reprCombo = useMemo(() => getReprCombo(msData), [msData])

  const getData = useCallback((filter) => {
    const postData = {
      ...filter,
      ROStsId: filter.ROStsId === -1 ? null: filter.ROStsId,
      IsNotFin: filter.ROStsId === -1 ? 1 : 0
    }
    ax.post(repairOrderApiNm.getRepairOrder, postData).then(value => {
      if (value.data) {
        lastFilter = postData
        setDataTable(addIdForDataGrid(value.data, "ROId"))
      }
    })
  }, [ax])

  const openDialog = useCallback((id) => {
    selectedId = id
    setDialogOpen(true)
  }, [])

  /**
   * Prints one ใบแจ้งซ่อม per checked row. The grid rows carry no photos, so the
   * with-images variant pulls each order's files first.
   */
  const handlePrint = useCallback((withImages) => async () => {
    const picked = dataTable.filter(row => selectionModel.includes(row.id))
    if (picked.length === 0) {
      alertError("กรุณาเลือกรายการที่ต้องการพิมพ์")
      return
    }
    let rows = picked
    if (withImages) {
      setIsPrinting(true)
      try {
        rows = await Promise.all(picked.map(async (row) => {
          const value = await ax.post(repairOrderApiNm.getRepairOrderDetail, { ROId: row.ROId })
          return { ...row, Files: value.data?.Files || [] }
        }))
      } finally {
        setIsPrinting(false)
      }
    }
    printRepairOrder({ msData, rows, withImages })
  }, [ax, dataTable, selectionModel, msData])

  const onFinish = useCallback(data => {
    if (data) {
      setDataTable(addIdForDataGrid(data, "ROId"))
    } else {
      getData(filterData)
    }
  }, [getData, filterData])

  useEffect(() => {
    getData({ ...initFilterData })
  }, [getData])

  return (
    <BoxFC height="100%" >
      <BoxFR>
        <AddButton onClick={() => openDialog(null)}>เพิ่มการแจ้งซ่อม</AddButton>
        <ButtonGroup>
          <Button variant="contained" color="secondary" disabled={isPrinting || selectionModel.length === 0}
            onClick={handlePrint(false)}>
            <PrintRounded sx={{ mr: 1 }} /> พิมพ์{selectionModel.length ? ` (${selectionModel.length})` : ""}
          </Button>
          <Button variant="contained" color="secondary" disabled={isPrinting || selectionModel.length === 0}
            onClick={handlePrint(true)}>
            พร้อมรูปภาพ
          </Button>
        </ButtonGroup>
        <Box flex={1} />
        <DateTimeSelect
          sx={{ width: 180 }}
          label=""
          start={filterData.ROTmSt}
          end={filterData.ROTmEn}
          setSelectDate={(st, en) => { setFilterData(o => ({ ...o, ROTmSt: st, ROTmEn: en })) }}
          getData={() => { getData(filterData) }}
        />
        <ComboBox sx={{ width: 150 }} options={msData.driverOnlyCombo} label={"พนักงานขับรถ"}
          selectedId={filterData.DrvId}
          setSelectedId={(id) => setFilterData(o => ({ ...o, DrvId: id }))} />

        <ComboBox sx={{ width: 150 }} options={msData.truckCombo} label={"เลขรถ"}
          selectedId={filterData.TukId}
          setSelectedId={(id) => setFilterData(o => ({ ...o, TukId: id }))} />
        <ComboBox sx={{ width: 170 }} options={reprCombo} label={repairOrderColNm.ReprId}
          selectedId={toReprComboId(filterData)}
          setSelectedId={(id) => setFilterData(o => ({ ...o, ...fromReprComboId(id) }))} />
        <TextField label="สถานะการซ่อม" sx={{ width: 200 }} size="small" select value={filterData.ROStsId}
          onChange={(e) => setFilterData(o => ({ ...o, ROStsId: e.target.value }))}>
          {msData.repairOrderStatuses.map((item) => (
            <MenuItem key={item.ROStsId} value={item.ROStsId}>
              {item.ROStsNm}
            </MenuItem>
          ))}
          <MenuItem key={-1} value={-1}>ยังไม่เสร็จสิ้นทั้งหมด</MenuItem>
        </TextField>
        <SearchButton onClick={() => getData(filterData)} />
        <ClearButton onClick={() => setFilterData({ ...clearFilterData })} />
      </BoxFR>

      <Box flex={1}>
        <DataGridCellExpand
          hideFooter
          checkboxSelection
          selectionModel={selectionModel}
          onSelectionModelChange={(newSelection) => setSelectionModel(newSelection)}
          rows={dataTable}
          columns={columns}
          onRowDoubleClick={(params, event, detail) => openDialog(params.id)}
        />
      </Box>
      <RepairOrderDialog
        selectedId={selectedId}
        dialogOpen={dialogOpen}
        setDialogOpen={setDialogOpen}
        onFinish={onFinish}
        lastFilter={lastFilter} />
    </BoxFC>
  );
}

export default RepairOrderManagement;